/**
 * #2671 — extracted from four independent reimplementations of name/email
 * substring search (the #2660 architecture-review judge report, item D-1):
 * the operator queue-view roster search, `manage-operators-table.component`'s
 * component-signal filter, `manage-testing-events.store`'s
 * `eventMatchesFilters`, and `manage-booking-table.component`'s
 * `MatTableDataSource.filterPredicate`. The judge found them structurally
 * different — not one shape reinvented four times — so this helper subsumes
 * both real shapes found rather than forcing a lowest-common-denominator:
 *
 *  - OR-of-fields (default): the term is a case-insensitive substring of AT
 *    LEAST ONE individual field. Used by the queue-view roster search, the
 *    events-list search (name OR region), and the bookings table filter
 *    (name/email/status/event/club).
 *  - joined-haystack (`joinFields: true`): all non-empty fields are joined
 *    with a single space into ONE haystack before matching, so a term CAN
 *    span a field boundary (e.g. "rios jamie" matching adjacent
 *    lastName+firstName). Used by the operators-table search.
 */
export interface MatchesSearchTermOptionsI {
  /**
   * true = join all non-empty fields into one space-separated haystack
   * before matching. false/omitted (default) = OR — the term must be a
   * substring of at least one individual field.
   */
  joinFields?: boolean;
}

/**
 * Case-insensitive, whitespace-trimmed substring match of `term` against
 * `fields`. A blank/whitespace-only term always matches (every call site
 * treats an empty search box as "no filter applied"). Null/undefined fields
 * are treated as empty strings, never thrown on.
 */
export function matchesSearchTerm(
  term: string,
  fields: ReadonlyArray<string | null | undefined>,
  options: MatchesSearchTermOptionsI = {},
): boolean {
  const normalized = term.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  if (options.joinFields) {
    const haystack = fields
      .filter((field): field is string => Boolean(field))
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalized);
  }

  return fields.some(field => (field ?? '').toLowerCase().includes(normalized));
}
